/**
 * The Ledger ErrorState: what failed, with its status ("Could not load traces
 * (502)"), what to do, and Retry.
 *
 * Two shapes:
 * - With the last data (`lastData`): an `err` Banner says what failed and
 *   when the data shown is from ("Showing data from 14:02"), with Retry as
 *   its action, and the last data stays visible under it.
 * - Without: a centred state in the content area, shaped like the
 *   EmptyState: a 20px icon, the title, one sentence, any server tips, Retry,
 *   and the docs link and error id from `ErrorActions`.
 *
 * The words come from the error presentation registry by code
 * (`features/errors/logic/presentation.ts`, through `resolveErrorCopy`);
 * `fallbackTitle` only names what was being done when the code is unknown. A
 * failure that never reached the server names the connection; everything else
 * names the server and shows the status.
 *
 * Spec: specs/design-system/ledger-data-display.feature
 */
import { Box, chakra, List, Stack, Text } from "@chakra-ui/react";
import { format, isSameDay } from "date-fns";
import { AlertCircle, RotateCw } from "lucide-react";
import type * as React from "react";
import { ErrorActions } from "~/features/errors/components/ErrorActions";
import { isServerUnreachable } from "~/features/errors/logic/isServerUnreachable";
import { readHandledError } from "~/features/errors/logic/readHandledError";
import { resolveErrorCopy } from "~/features/errors/logic/resolveErrorCopy";
import { Banner } from "./banner";
import { Button } from "./button";

export interface ErrorStateProps {
  /** Any error, handled or not. */
  error: unknown;
  /** The headline when the registry has no copy: "Could not load traces". */
  fallbackTitle?: string;
  /** Hard override of the headline. Rare. */
  title?: string;
  /** Asks again. Shown as "Retry". */
  onRetry?: () => void;
  /** The retry is running: the button shows its spinner. */
  retrying?: boolean;
  /**
   * Retry is shown but cannot run, with the reason in its tooltip ("Needs a
   * connection" while offline). The button stays, disabled.
   */
  retryDisabledReason?: string;
  /** The data from before the failure, kept visible under the banner. */
  lastData?: React.ReactNode;
  /** When `lastData` was loaded. */
  lastUpdatedAt?: Date | number;
  /**
   * The banner's sentence about the data kept, given the formatted time it
   * was loaded ("14:02"). Defaults to "Showing data from 14:02." Use it when
   * the kept data is not what was asked for (another page, other filters).
   */
  lastDataNote?: (loadedAt: string | undefined) => React.ReactNode;
  /** Replaces the alert glyph of the centred shape. */
  icon?: React.ReactNode;
  /** More ways forward under Retry (a link home, a sign-in). */
  actions?: React.ReactNode;
  /** Appends the status to the headline. On by default. */
  showStatus?: boolean;
  /** Fill the viewport (a page with nothing else on it). */
  fullHeight?: boolean;
}

/** The HTTP status a failure carries, if any. */
export function errorStatus(error: unknown): number | undefined {
  const handled = readHandledError(error);
  if (handled?.httpStatus) return handled.httpStatus;
  if (typeof error !== "object" || error === null) return undefined;
  const candidate = error as {
    data?: { httpStatus?: unknown } | null;
    status?: unknown;
    statusCode?: unknown;
  };
  for (const value of [
    candidate.data?.httpStatus,
    candidate.status,
    candidate.statusCode,
  ]) {
    if (typeof value === "number" && value >= 400) return value;
  }
  return undefined;
}

const formatFrom = (value: Date | number) => {
  const date = value instanceof Date ? value : new Date(value);
  return isSameDay(date, new Date())
    ? format(date, "HH:mm")
    : format(date, "d MMM, HH:mm");
};

function headline({
  error,
  title,
  fallbackTitle,
  showStatus,
}: {
  error: unknown;
  title?: string;
  fallbackTitle?: string;
  showStatus: boolean;
}) {
  const copy = resolveErrorCopy({ error, title, fallbackTitle });
  if (isServerUnreachable(error)) {
    return {
      copy,
      text: title ?? "Could not reach the server",
      description: "Check your connection, then retry.",
    };
  }
  const status = showStatus ? errorStatus(error) : undefined;
  const text =
    status && !copy.title.includes(String(status))
      ? `${copy.title} (${status})`
      : copy.title;
  return { copy, text, description: copy.description };
}

export function ErrorState({
  error,
  fallbackTitle,
  title,
  onRetry,
  retrying,
  retryDisabledReason,
  lastData,
  lastUpdatedAt,
  lastDataNote,
  icon,
  actions,
  showStatus = true,
  fullHeight = false,
}: ErrorStateProps) {
  const { copy, text, description } = headline({
    error,
    title,
    fallbackTitle,
    showStatus,
  });

  const retry = onRetry ? (
    <Button
      // A 32px touch target below 1024; the compact 24px from 1024.
      size={{ base: "lg", lg: "xs" }}
      variant="secondary"
      onClick={onRetry}
      busy={retrying}
      disabledReason={retryDisabledReason}
    >
      <RotateCw aria-hidden="true" />
      Retry
    </Button>
  ) : null;

  if (lastData !== undefined && lastData !== null && lastData !== false) {
    return (
      <Stack gap="var(--space-3)" data-ledger="error-state" data-shape="banner">
        <Banner tone="err" title={text} action={retry}>
          {lastDataNote
            ? lastDataNote(
                lastUpdatedAt !== undefined
                  ? formatFrom(lastUpdatedAt)
                  : undefined,
              )
            : lastUpdatedAt !== undefined
              ? `Showing data from ${formatFrom(lastUpdatedAt)}.`
              : "Showing the last data loaded."}
        </Banner>
        {lastData}
      </Stack>
    );
  }

  return (
    <Box
      data-ledger="error-state"
      data-shape="centred"
      display="flex"
      alignItems={fullHeight ? "center" : "flex-start"}
      justifyContent="center"
      width="full"
      paddingTop="var(--space-8)"
      paddingBottom="var(--space-6)"
      paddingInline="var(--space-4)"
      minHeight={fullHeight ? "100vh" : undefined}
    >
      <Stack
        gap="var(--space-2)"
        align="center"
        textAlign="center"
        maxWidth="var(--layout-prose-max)"
      >
        <Box
          aria-hidden="true"
          color="var(--color-err)"
          css={{
            "& svg": { width: "var(--icon-lg)", height: "var(--icon-lg)" },
          }}
          marginBottom="var(--space-1)"
        >
          {icon ?? <AlertCircle strokeWidth={1.75} />}
        </Box>
        {/* `role="alert"` on the words, not the box: a screen reader hears
            what went wrong, not the decoration around it. */}
        <Stack gap="var(--space-1)" role="alert" align="center">
          <chakra.h3
            fontSize="var(--text-14)"
            fontWeight="var(--weight-semibold)"
            lineHeight="var(--leading-tight)"
            color="var(--color-fg)"
            margin="0"
          >
            {text}
          </chakra.h3>
          {description ? (
            <Text fontSize="var(--text-13)" color="var(--color-fg-2)">
              {description}
            </Text>
          ) : null}
        </Stack>
        {copy.tips.length > 0 ? (
          <List.Root
            gap="var(--space-1)"
            fontSize="var(--text-13)"
            color="var(--color-fg-2)"
            textAlign="start"
            paddingInlineStart="var(--space-4)"
          >
            {/* Index key: tips are server prose and two can be identical. */}
            {copy.tips.map((tip, index) => (
              <List.Item key={index}>{tip}</List.Item>
            ))}
          </List.Root>
        ) : null}
        {retry || actions ? (
          <Stack
            gap="var(--space-2)"
            align="center"
            paddingTop="var(--space-2)"
            width="full"
          >
            {retry}
            {actions}
          </Stack>
        ) : null}
        <ErrorActions
          docsUrl={copy.docsUrl}
          traceId={copy.traceId}
          color="var(--color-fg-3)"
          accentColor="var(--color-link)"
        />
      </Stack>
    </Box>
  );
}
